import type { ComboPricingRule } from '../policy/business-policy.ts';

export interface PricingUnit {
  readonly lineId: string;
  readonly unitIndex: number;
  readonly productId: string;
  readonly selectorKeys: readonly string[];
  readonly baseMinor: number;
}

export interface ComboMatch {
  readonly ruleId: string;
  readonly consumedUnits: readonly { readonly lineId: string; readonly unitIndex: number }[];
  /** Applied combo base total; historical field name retained for contract compatibility. */
  readonly fixedBaseTotalMinor: number;
  readonly originalBaseTotalMinor: number;
}

const requirementSize = (rule: ComboPricingRule): number =>
  rule.requirements.reduce((sum, requirement) => sum + requirement.quantity, 0);

const validateRule = (rule: ComboPricingRule): void => {
  if (!rule.ruleId || !Number.isSafeInteger(rule.priority) ||
      !Number.isSafeInteger(rule.fixedBaseTotalMinor) || rule.fixedBaseTotalMinor < 0 ||
      rule.requirements.length === 0) {
    throw new Error(`COMBO_RULE_INVALID:${rule.ruleId || 'unknown'}`);
  }
  for (const requirement of rule.requirements) {
    if (!requirement.selectorKey.trim() || !Number.isSafeInteger(requirement.quantity) || requirement.quantity <= 0) {
      throw new Error(`COMBO_REQUIREMENT_INVALID:${rule.ruleId}`);
    }
  }
  if (rule.componentProductMinor) {
    const entries = Object.entries(rule.componentProductMinor);
    if (entries.length === 0) throw new Error(`COMBO_COMPONENT_PRICING_INVALID:${rule.ruleId}`);
    for (const [productId, amountMinor] of entries) {
      if (!productId.trim() || !Number.isSafeInteger(amountMinor) || amountMinor! < 0) {
        throw new Error(`COMBO_COMPONENT_PRICING_INVALID:${rule.ruleId}`);
      }
    }
  }
};

const compareRules = (left: ComboPricingRule, right: ComboPricingRule): number => {
  if (left.priority !== right.priority) return right.priority - left.priority;
  const sizeDifference = requirementSize(right) - requirementSize(left);
  if (sizeDifference !== 0) return sizeDifference;
  return left.ruleId.localeCompare(right.ruleId);
};

const unitKey = (unit: Pick<PricingUnit, 'lineId' | 'unitIndex'>): string => `${unit.lineId}#${unit.unitIndex}`;

const componentAmountFor = (rule: ComboPricingRule, unit: PricingUnit): number | null => {
  if (!rule.componentProductMinor) return null;
  if (!Object.prototype.hasOwnProperty.call(rule.componentProductMinor, unit.productId)) return null;
  const amount = rule.componentProductMinor[unit.productId];
  return Number.isSafeInteger(amount) && amount! >= 0 ? amount! : null;
};

const unitEligibleForRule = (rule: ComboPricingRule, unit: PricingUnit): boolean =>
  !rule.componentProductMinor || componentAmountFor(rule, unit) !== null;

const selectOne = (
  available: ReadonlyMap<string, PricingUnit>,
  rule: ComboPricingRule,
): readonly PricingUnit[] | null => {
  const selected: PricingUnit[] = [];
  const reserved = new Set<string>();
  for (const requirement of rule.requirements) {
    for (let count = 0; count < requirement.quantity; count += 1) {
      const candidate = [...available.values()].find((unit) =>
        unit.selectorKeys.includes(requirement.selectorKey) &&
        unitEligibleForRule(rule, unit) &&
        !reserved.has(unitKey(unit)));
      if (!candidate) return null;
      selected.push(candidate);
      reserved.add(unitKey(candidate));
    }
  }
  return selected;
};

const appliedComboBaseTotalMinor = (rule: ComboPricingRule, selected: readonly PricingUnit[]): number => {
  if (!rule.componentProductMinor) return rule.fixedBaseTotalMinor;
  const total = selected.reduce((sum, unit) => {
    const amount = componentAmountFor(rule, unit);
    if (amount === null) throw new Error(`COMBO_COMPONENT_PRICE_UNBOUND:${rule.ruleId}:${unit.productId}`);
    return sum + amount;
  }, 0);
  if (!Number.isSafeInteger(total)) throw new Error(`COMBO_COMPONENT_TOTAL_INVALID:${rule.ruleId}`);
  return total;
};

export function resolveComboMatches(
  units: readonly PricingUnit[],
  rules: readonly ComboPricingRule[],
): readonly ComboMatch[] {
  const available = new Map(units.map((unit) => [unitKey(unit), unit]));
  const matches: ComboMatch[] = [];
  const ordered = [...rules.filter((rule) => rule.enabled)].sort(compareRules);
  for (const rule of ordered) {
    validateRule(rule);
    for (let iteration = 0; iteration < units.length; iteration += 1) {
      const selected = selectOne(available, rule);
      if (!selected) break;
      for (const unit of selected) available.delete(unitKey(unit));
      matches.push({
        ruleId: rule.ruleId,
        consumedUnits: selected.map((unit) => ({ lineId: unit.lineId, unitIndex: unit.unitIndex })),
        fixedBaseTotalMinor: appliedComboBaseTotalMinor(rule, selected),
        originalBaseTotalMinor: selected.reduce((sum, unit) => sum + unit.baseMinor, 0),
      });
    }
  }
  return matches;
}
